/**
 * MfaPanel — Settings section for TOTP MFA management. Only rendered for
 * full-admin (role === "admin") because backend endpoints are gated by
 * `require_full_admin`.
 *
 * States:
 *   - loading         initial /status fetch
 *   - disabled        MFA off → show "Aktifkan MFA" button
 *   - enrolling       showing QR + OTP input (post /enroll/start)
 *   - enabled         MFA on → show "Nonaktifkan MFA" (requires OTP)
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, QrCode, WarningOctagon, CheckCircle } from "@phosphor-icons/react";

export default function MfaPanel({ onEnrolled = null }) {
  const [status, setStatus] = useState("loading");   // loading | disabled | enrolling | enabled
  const [qr, setQr] = useState(null);                // { qr_png_base64, provisioning_uri, secret_hint }
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState(null);        // org-wide enforcement policy
  const [policyBusy, setPolicyBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await api.get("/auth/mfa/status");
      setStatus(r.data.enabled ? "enabled" : "disabled");
    } catch (err) {
      // 403 = user isn't full admin. Silent — panel just won't render.
      setStatus("hidden");
    }
  };

  const loadPolicy = async () => {
    try {
      const r = await api.get("/auth/mfa/policy");
      setPolicy(r.data);
    } catch (err) { /* non-full-admin — ignore */ }
  };

  useEffect(() => { loadStatus(); loadPolicy(); }, []);

  const savePolicy = async (next) => {
    setPolicyBusy(true);
    try {
      const r = await api.put("/auth/mfa/policy", {
        enabled: next.enabled,
        grace_period_days: next.grace_period_days,
      });
      setPolicy(r.data);
      toast.success(next.enabled ? "Enforcement MFA diaktifkan" : "Enforcement MFA dimatikan");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setPolicyBusy(false); }
  };

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/enroll/start");
      setQr(r.data);
      setStatus("enrolling");
      setOtp("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const verifyEnroll = async () => {
    if (!/^\d{6}$/.test(otp)) {
      toast.error("OTP harus 6 digit angka");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/mfa/enroll/verify", { otp });
      toast.success("MFA berhasil diaktifkan");
      setQr(null);
      setOtp("");
      setStatus("enabled");
      if (onEnrolled) onEnrolled();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!/^\d{6}$/.test(otp)) {
      toast.error("OTP harus 6 digit angka");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { otp });
      toast.success("MFA dinonaktifkan");
      setOtp("");
      setStatus("disabled");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (status === "hidden" || status === "loading") return null;

  return (
    <div className="border-2 border-border bg-card p-6 rounded-xl space-y-4" data-testid="mfa-panel">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
          <ShieldCheck size={22} weight="bold" />
        </span>
        <div>
          <h2 className="text-xl font-black tracking-tight">Two-Factor Authentication (MFA)</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Tambahkan lapisan verifikasi kedua saat login — mencegah account
            takeover kalau password bocor.
          </p>
        </div>
      </div>

      {status === "disabled" && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-muted/30">
          <WarningOctagon size={22} className="text-yellow-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold">MFA belum aktif untuk akun ini.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Kami sangat menyarankan admin mengaktifkan MFA. Anda akan butuh
              app Authenticator (Google Authenticator, 1Password, Authy, dll.)
              di ponsel.
            </p>
          </div>
          <Button
            onClick={startEnroll}
            disabled={busy}
            className="rounded-xl h-10 font-bold uppercase tracking-wider shrink-0"
            data-testid="mfa-enable-btn"
          >
            Aktifkan MFA
          </Button>
        </div>
      )}

      {status === "enrolling" && qr && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-xl border border-primary/30 bg-primary/5">
            <QrCode size={20} className="text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              1. Scan QR di bawah pakai authenticator app <br />
              2. Ketik 6-digit OTP yang muncul untuk konfirmasi <br />
              3. Simpan kode-kode backup di tempat aman
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <img
              src={`data:image/png;base64,${qr.qr_png_base64}`}
              alt="MFA QR"
              className="w-48 h-48 border-2 border-border rounded-xl bg-white p-2"
              data-testid="mfa-qr-image"
            />
            <div className="flex-1 space-y-3 min-w-0">
              <div>
                <Label className="overline">Manual entry (kalau tidak bisa scan)</Label>
                <p className="mt-1 text-xs font-mono bg-muted p-2 rounded break-all" data-testid="mfa-secret-hint">
                  {qr.secret_hint}
                </p>
              </div>
              <div>
                <Label htmlFor="mfa-otp-verify" className="overline">Masukkan 6-digit OTP</Label>
                <Input
                  id="mfa-otp-verify"
                  data-testid="mfa-otp-input"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="rounded-xl h-11 font-mono text-center text-lg tracking-widest mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={verifyEnroll}
                  disabled={busy || otp.length !== 6}
                  className="rounded-xl h-11 font-bold uppercase tracking-wider flex-1"
                  data-testid="mfa-verify-btn"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Verifikasi & Aktifkan
                </Button>
                <Button
                  onClick={() => { setStatus("disabled"); setQr(null); setOtp(""); }}
                  disabled={busy}
                  variant="outline"
                  className="rounded-xl h-11"
                >
                  Batal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {status === "enabled" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 rounded-xl border border-emerald-300 bg-emerald-50">
            <CheckCircle size={20} weight="bold" className="text-emerald-600" />
            <p className="text-sm font-bold text-emerald-800">MFA aktif untuk akun ini.</p>
          </div>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <Label htmlFor="mfa-otp-disable" className="overline">
                OTP saat ini (diperlukan untuk nonaktifkan)
              </Label>
              <Input
                id="mfa-otp-disable"
                data-testid="mfa-disable-otp-input"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="rounded-xl h-11 font-mono text-center text-lg tracking-widest mt-1"
              />
            </div>
            <Button
              onClick={disable}
              disabled={busy || otp.length !== 6}
              variant="destructive"
              className="rounded-xl h-11 font-bold uppercase tracking-wider"
              data-testid="mfa-disable-btn"
            >
              Nonaktifkan MFA
            </Button>
          </div>
        </div>
      )}

      {/* ── Org-wide enforcement policy (full-admin only) ─────────────── */}
      {policy && (
        <div className="mt-2 pt-4 border-t border-border space-y-3" data-testid="mfa-policy-config">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold">Wajibkan MFA untuk semua Admin</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Setiap full-admin harus mengaktifkan MFA dalam masa tenggang. Setelah
                itu, admin tanpa MFA dipaksa setup sebelum bisa memakai fitur lain.
              </p>
            </div>
            <Button
              onClick={() => savePolicy({ enabled: !policy.enabled, grace_period_days: policy.grace_period_days })}
              disabled={policyBusy}
              variant={policy.enabled ? "destructive" : "default"}
              className="rounded-xl h-10 font-bold uppercase tracking-wider shrink-0"
              data-testid="mfa-policy-toggle-btn"
            >
              {policy.enabled ? "Matikan" : "Aktifkan"}
            </Button>
          </div>
          <div className="flex items-end gap-3">
            <div className="w-40">
              <Label htmlFor="mfa-grace-days" className="overline">Masa tenggang (hari)</Label>
              <Input
                id="mfa-grace-days"
                data-testid="mfa-grace-days-input"
                type="number" min={1} max={90}
                value={policy.grace_period_days}
                onChange={(e) => setPolicy({ ...policy, grace_period_days: parseInt(e.target.value || "1", 10) })}
                className="rounded-xl h-10 mt-1"
              />
            </div>
            <Button
              onClick={() => savePolicy({ enabled: policy.enabled, grace_period_days: policy.grace_period_days })}
              disabled={policyBusy}
              variant="outline"
              className="rounded-xl h-10"
              data-testid="mfa-grace-save-btn"
            >
              Simpan
            </Button>
            {policy.enabled && (
              <p className="text-xs text-muted-foreground pb-2" data-testid="mfa-policy-status">
                Aktif sejak {policy.enforced_at ? new Date(policy.enforced_at).toLocaleDateString("id-ID") : "-"}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
